import { useFormContext, useWatch } from 'react-hook-form';
import styled from 'styled-components';

import { CopyValue } from 'components/CopyValue';
import {
    DetailFormCheckbox,
    DetailFormImage,
    DetailFormSelect,
    DetailFormTextArea,
    DetailFormValue,
    DetailValue,
} from 'components/DetailsView';
import { DetailDisplayValue } from 'components/DetailsView/DetailDisplayValue';
import { ColumnsSectionContainer, SectionColumn, SectionContainer } from 'components/DetailsView/Section';
import { getExtendedFormattedDatetime } from 'helpers/dateTimes';
import { getProductFullName } from 'pages/Products/utils';

import { GetDetailedEquipmentQuery } from 'data/__generated__';

export const EquipmentFormContent = ({
    equipment,
    supplierOptions = [],
}: {
    // no equipment provided => creation mode (blank form, no id/image/dates/supplier)
    equipment?: NonNullable<GetDetailedEquipmentQuery['detailedEquipment']>;
    supplierOptions?: { label: string; value: string }[];
}) => {
    const { name, brand, volume, price, description, hidden, height, width, length, weight } = equipment ?? {};

    // Equipment suppliers are handled as a single "fournisseur" (only the first is used).
    const defaultSupplierId = equipment?.suppliers?.[0]?._id ?? undefined;

    const { control, register } = useFormContext();
    const updatedTitleValues: string[] = useWatch({
        control,
        name: ['name', 'brand', 'volume'],
        defaultValue: [name, brand, volume],
    });

    return (
        <SectionContainer title="Informations générales" isInitiallyOpen>
            <ColumnsSectionContainer numberOfColumns={3}>
                <SectionColumn>
                    {equipment && <CopyValue value={equipment._id} label="Id" />}
                    <Row>
                        {equipment && (
                            <DetailFormImage
                                name="image"
                                label="Photo"
                                sublabel="Image de l'équipement"
                                defaultValue={equipment.imageUrl}
                                size={200}
                            />
                        )}
                        <Column>
                            {equipment && (
                                <>
                                    <DetailValue
                                        label="Date de création"
                                        value={getExtendedFormattedDatetime(equipment.createdAt)}
                                    />
                                    <DetailValue
                                        label="Dernière mise à jour"
                                        value={getExtendedFormattedDatetime(equipment.updatedAt)}
                                    />
                                </>
                            )}
                            <DetailFormCheckbox
                                label="Caché"
                                sublabel="Équipement ne sera visible sur l'app si caché"
                                name="hidden"
                                defaultChecked={hidden ?? false}
                            />
                        </Column>
                    </Row>
                    <DetailFormValue
                        label="Prix"
                        sublabel="En €"
                        type="number"
                        defaultValue={price}
                        {...register('price', { valueAsNumber: true })}
                    />
                    {equipment && (
                        <DetailFormSelect<string>
                            label="Fournisseur"
                            name="supplierId"
                            placeholder="Sélectionner un fournisseur"
                            defaultValue={defaultSupplierId}
                            options={supplierOptions}
                            isClearable
                        />
                    )}
                </SectionColumn>
                <SectionColumn>
                    <DetailFormValue
                        label="Nom"
                        defaultValue={name}
                        {...register('name', { required: true })}
                        width="100%"
                    />
                    <DetailFormValue label="Marque" defaultValue={brand} {...register('brand')} />
                    <DetailFormValue
                        label="Capacité"
                        sublabel="(capacité utilisateurs, affiché aux clients)"
                        defaultValue={volume}
                        {...register('volume')}
                    />
                    <DetailDisplayValue
                        label="Nom complet"
                        sublabel="(calculé)"
                        value={getProductFullName({
                            name: updatedTitleValues[0] || name || '',
                            brand: updatedTitleValues[1] || brand,
                            volume: updatedTitleValues[2] || volume,
                        })}
                    />
                </SectionColumn>
                <SectionColumn>
                    <DetailFormTextArea
                        label="Description"
                        defaultValue={description}
                        {...register('description', { required: true })}
                        width="100%"
                        height="130px"
                    />
                    <DetailFormValue
                        label="Poids"
                        type="number"
                        step=".01"
                        defaultValue={weight}
                        {...register('weight', { valueAsNumber: true })}
                    />
                    <DetailFormValue
                        label="Hauteur"
                        type="number"
                        step=".01"
                        defaultValue={height}
                        {...register('height', { valueAsNumber: true })}
                    />
                    <DetailFormValue
                        label="Largeur"
                        type="number"
                        step=".01"
                        defaultValue={width}
                        {...register('width', { valueAsNumber: true })}
                    />
                    <DetailFormValue
                        label="Longueur"
                        type="number"
                        step=".01"
                        defaultValue={length ?? 0}
                        {...register('length', { valueAsNumber: true })}
                    />
                </SectionColumn>
            </ColumnsSectionContainer>
        </SectionContainer>
    );
};

const Row = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;

    & > :not(:first-child) {
        margin-left: 10px;
    }
`;

const Column = styled.div`
    display: flex;
    flex-direction: column;

    & > :not(:first-child) {
        margin-top: 15px;
    }
`;
